import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useModal } from "@/context/ModalContext";
import { MARKET } from "@/market";

export function Hero() {
  const { t } = useTranslation();
  const { open } = useModal();
  const d = (key: string) => t(`hero.dashboard.${key}`);

  return (
    <section id="top" className="relative min-h-screen bg-dark-bg overflow-hidden pt-24 flex items-center">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.1] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,173,181,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,173,181,1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative w-full mx-auto max-w-[1200px] px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-[55%_45%] gap-10 lg:gap-12 items-center">
        {/* Left: Text */}
        <div>
          <h1 className="text-white font-bold tracking-[-0.03em] leading-[1.05] text-[36px] sm:text-[52px] lg:text-[64px]">
            {t("hero.title_1")}
            <br />
            <span className="text-gradient">{t("hero.title_2")}</span> {t("hero.title_3")}
          </h1>

          <p className="mt-5 text-white/60 text-[16px] sm:text-[18px] leading-[1.7] max-w-[480px]">
            {t("hero.subtitle")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
            <button
              onClick={open}
              className="inline-flex items-center justify-center h-[52px] px-7 rounded-lg bg-primary hover:bg-primary-dark text-white text-[16px] font-semibold transition-all hover:scale-[1.02]"
            >
              {t("hero.cta_primary")}
            </button>
            <a
              href="#how"
              className="inline-flex items-center justify-center gap-2 h-[52px] px-4 text-white/80 hover:text-white text-[16px] font-medium transition-colors"
            >
              {t("hero.cta_secondary")} <span aria-hidden="true">→</span>
            </a>
          </div>

          <p className="mt-4 text-[13px] text-white/40">{t("hero.trust")}</p>

          {/* Trust stats */}
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2.5">
            {[
              { bold: t("hero.stat_venues_val"), text: t("hero.stat_venues") },
              { bold: t("hero.stat_country"), text: "" },
              { bold: t("hero.stat_payments"), text: "" },
              { bold: t("hero.stat_trial_val"), text: t("hero.stat_trial") },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[13px] text-white/50">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
                <span>
                  <span className="text-white font-semibold">{item.bold}</span>
                  {item.text && <span className="ml-1">{item.text}</span>}
                </span>
                {i < 3 && <span className="hidden sm:inline-block w-px h-4 bg-dark-border ml-2" />}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Dashboard mockup — floating + tilted */}
        <div className="relative hidden lg:flex items-center justify-center">
          {/* Glow */}
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-4/5 h-20 bg-primary/30 blur-3xl rounded-full pointer-events-none" />

          <motion.div
            className="relative w-full rounded-2xl bg-dark-card border border-dark-border shadow-2xl shadow-black/60 overflow-hidden"
            style={{ transformPerspective: 1200 }}
            animate={{
              y: [0, -12, 0],
              rotateX: [4, 4, 4],
              rotateY: [-3, -3, -3],
            }}
            transition={{
              y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
              rotateX: { duration: 0 },
              rotateY: { duration: 0 },
            }}
          >
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 h-9 border-b border-dark-border bg-dark-bg/60">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              <span className="ml-3 text-[11px] text-white/30">{d("url")}</span>
            </div>

            {/* Dashboard body */}
            <div className="grid grid-cols-12 gap-2.5 p-3">
              {/* Sidebar */}
              <div className="col-span-3 rounded-lg bg-dark-bg border border-dark-border p-2 space-y-1">
                <div className="text-[10px] px-2 py-1.5 rounded bg-primary/15 text-primary font-medium">
                  {d("home")}
                </div>
                {[d("orders"), d("menu"), d("kitchen"), d("analytics"), d("staff")].map((item) => (
                  <div key={item} className="text-[10px] px-2 py-1.5 rounded text-white/35">
                    {item}
                  </div>
                ))}
              </div>

              {/* Main area */}
              <div className="col-span-9 space-y-2.5">
                {/* Revenue card */}
                <div className="rounded-lg bg-dark-bg border border-dark-border p-3">
                  <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">{d("revenue_label")}</div>
                  <div className="flex items-end justify-between">
                    <div className="text-white text-[22px] font-bold font-raleway leading-none">
                      {MARKET.mockMoney(4820000)} <span className="text-[11px] text-white/40 font-normal">{d("currency")}</span>
                    </div>
                    <div className="text-[11px] text-green-400 font-semibold">↑ 12.4%</div>
                  </div>
                  <div className="flex items-end gap-0.5 h-10 mt-2">
                    {[40, 60, 35, 80, 50, 90, 65, 85, 55, 95, 70, 88].map((h, i) => (
                      <div key={i} className="flex-1 rounded-sm bg-primary/40" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: d("orders_count"), value: "47", color: "text-accent" },
                    { label: d("avg_check"), value: MARKET.mockMoney(102000, { short: true }), color: "text-primary" },
                    { label: d("avg_time"), value: d("avg_time_val"), color: "text-green-400" },
                  ].map((card) => (
                    <div key={card.label} className="rounded-lg bg-dark-bg border border-dark-border p-2.5">
                      <div className="text-[9px] text-white/30 mb-1">{card.label}</div>
                      <div className={`text-[14px] font-bold font-raleway ${card.color}`}>{card.value}</div>
                    </div>
                  ))}
                </div>

                {/* Orders list */}
                <div className="rounded-lg bg-dark-bg border border-dark-border p-2.5 space-y-2">
                  {[
                    { table: `${d("table")} 3`, status: d("paid"), color: "text-green-400", sum: MARKET.mockMoney(48000) },
                    { table: `${d("table")} 7`, status: d("waiting"), color: "text-accent", sum: MARKET.mockMoney(125000) },
                    { table: `${d("table")} 12`, status: d("order"), color: "text-primary", sum: MARKET.mockMoney(67000) },
                  ].map((row) => (
                    <div key={row.table} className="flex items-center justify-between text-[11px]">
                      <span className="text-white/60 w-14">{row.table}</span>
                      <span className={`${row.color} font-medium`}>{row.status}</span>
                      <span className="text-white/40">
                        {row.sum} {d("currency")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
